import type { Reach } from "./roleScope";
import type {
  Role,
  RoleDataById,
  RoleDeactivateImpactRead,
} from "./roles-permissions.queries.types";

/** `ROLE_SCOPES` in the backend (userManagement/types.ts); the access engine decides what each
 *  one grants at a company login vs the ServEngine login. */
export type RoleLevel = "entity" | "servengine" | "both" | "servengine_all";

export const LEVEL_BADGE_LABEL: Record<RoleLevel, string> = {
  entity: "Entity",
  servengine: "ServEngine",
  both: "Both",
  servengine_all: "All companies",
};

export const LEVEL_DETAIL_LABEL: Record<RoleLevel, string> = {
  entity: "Entity level",
  servengine: "ServEngine level",
  both: "Entity and ServEngine level",
  servengine_all: "All companies",
};

export type RoleDirectoryFields = {
  level: RoleLevel | null;
  department: string | null;
  deactivatedAt: string | null;
  deactivatedBy: string | null;
  /** How far the people holding this role can see; `null` when the role has none set. */
  reach: Reach | null;
};

export type RoleWithDirectory = Omit<RoleDataById, keyof RoleDirectoryFields> & RoleDirectoryFields;

export type RoleListDirectoryFields = {
  level: RoleLevel | null;
  department: string | null;
  peopleCount: number | null;
  scopeBoxes: string[] | null;
  reach: string | null;
};

export type RoleListItemWithDirectory = Omit<Role, keyof RoleListDirectoryFields> &
  RoleListDirectoryFields;

export type RoleHolderLosingAccess = {
  name: string;
  entity: string;
};

/** `holders: null` means the back end has not told us yet — say nothing rather than "no one". */
export type RoleDeactivateImpact = {
  holders: number | null;
  namedHolders: string[];
  /** Who the deactivate would leave with no role; `null` until the deactivate-impact read answers. */
  losingAllAccess: RoleHolderLosingAccess[] | null;
};

/** The first page of the role's holders, for the Deactivate modal; `null` while it loads or fails. */
export type RoleHoldersPreview = {
  /** Distinct people still holding the role; `null` when the first page does not hold every row. */
  people: number | null;
  names: string[];
};

export interface RolesDirectoryClient {
  decorate(role: RoleDataById): RoleWithDirectory;
  /**
   * Who last saved the role, for the Access tab's save-conflict alert. `null` when the read names
   * no one: the alert then names the role and the time instead of guessing a person.
   */
  getLastSavedBy(role: Pick<RoleDataById, "updatedBy">): string | null;
  decorateListRole(role: Role): RoleListItemWithDirectory;
  getDeactivateImpact(
    role: Role,
    holders: RoleHoldersPreview | null,
    impact: RoleDeactivateImpactRead | null,
  ): RoleDeactivateImpact;
}
